import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideArrowDown, LucideArrowUp } from '@lucide/angular';
import {
  ActivityCategory,
  ActivityType,
  ActivityTypeKind,
  CreateActivityTypeRequest,
  ScheduleType,
  UpdateActivityTypeRequest
} from './activity-type.model';
import { ActivityTypeService } from './activity-type.service';
import { describeGenericApiError } from '../../core/http/api-error.util';
import { ConfirmDialogService } from '../../core/ui/confirm-dialog.service';
import { MeService } from '../settings/me.service';
import { ScheduleGroup } from '../settings/me.model';
import { getEnabledScheduleGroups, resolveScheduleGroupLabel } from '../today/schedule-group.util';

/**
 * The ActivitiesComponent is responsible for managing the user's activity types. It allows users
 * to create, edit, and delete activity types, which can be either time blocks or habit counters.
 * The component interacts with the ActivityTypeService to perform CRUD operations and provides
 * feedback to the user through loading indicators, success messages, and error handling.
 *
 * @author sadatmalik
 */
@Component({
  selector: 'app-activities',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideArrowUp, LucideArrowDown],
  templateUrl: './activities.component.html',
  styleUrls: ['./activities.component.scss'],
})
export class ActivitiesComponent implements OnInit {
  private readonly activityTypeService = inject(ActivityTypeService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly meService = inject(MeService);

  readonly activityTypes = signal<ActivityType[]>([]);
  readonly initialLoading = signal(false);
  readonly refreshing = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly saving = signal(false);
  readonly reordering = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly deletingById = signal<Record<string, boolean>>({});
  readonly scheduleGroups = signal<ScheduleGroup[]>([
    { ordinal: 'PRIMARY', label: null, targetEndLocalTime: null, enabled: true },
    { ordinal: 'SECONDARY', label: null, targetEndLocalTime: null, enabled: true },
    { ordinal: 'TERTIARY', label: null, targetEndLocalTime: null, enabled: false }
  ]);

  name = '';
  kind: ActivityTypeKind = 'TIME_BLOCK';
  category: ActivityCategory = 'WORK';
  scheduleType: ScheduleType = 'PRIMARY';
  defaultMinutes: number | null = null;

  readonly kinds: ActivityTypeKind[] = ['TIME_BLOCK', 'HABIT_COUNTER'];
  readonly categories: ActivityCategory[] = ['WORK', 'PERSONAL'];
  readonly minDefaultMinutes = 1;
  readonly maxDefaultMinutes = 480;
  readonly isEditing = computed(() => this.editingId() !== null);
  readonly timeBlockActivityTypes = computed(() =>
    this.activityTypes()
      .filter((activityType) => activityType.kind === 'TIME_BLOCK')
      .slice()
      .sort((left, right) => left.displayOrder - right.displayOrder)
  );
  readonly habitActivityTypes = computed(() =>
    this.activityTypes()
      .filter((activityType) => activityType.kind === 'HABIT_COUNTER')
      .slice()
      .sort((left, right) => left.displayOrder - right.displayOrder)
  );
  readonly enabledScheduleTypes = computed(() => {
    const enabled = getEnabledScheduleGroups(this.scheduleGroups()).map((group) => group.ordinal);
    return enabled.includes(this.scheduleType) ? enabled : [...enabled, this.scheduleType];
  });

  ngOnInit(): void {
    this.loadScheduleGroups();
    this.load();
  }

  load(): void {
    this.initialLoading.set(true);
    this.error.set(null);
    this.activityTypeService.list().subscribe({
      next: (activityTypes) => {
        this.activityTypes.set(activityTypes);
        this.initialLoading.set(false);
      },
      error: (err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to load activity types'));
        this.initialLoading.set(false);
      },
    });
  }

  startCreate(): void {
    this.clearForm();
    this.editingId.set(null);
    this.error.set(null);
    this.success.set(null);
  }

  startEdit(activityType: ActivityType): void {
    this.name = activityType.name;
    this.kind = activityType.kind;
    this.category = activityType.category;
    this.scheduleType = activityType.scheduleType;
    this.defaultMinutes = activityType.defaultMinutes;
    this.editingId.set(activityType.id);
    this.error.set(null);
    this.success.set(null);
  }

  cancelEdit(): void {
    this.startCreate();
  }

  submit(): void {
    const trimmedName = this.name.trim();
    if (trimmedName.length === 0) {
      this.error.set('Name is required.');
      return;
    }
    const isTimeBlock = this.kind === 'TIME_BLOCK';
    if (isTimeBlock && this.defaultMinutes != null && (this.defaultMinutes < this.minDefaultMinutes || this.defaultMinutes > this.maxDefaultMinutes)) {
      this.error.set(`Default minutes must be between ${this.minDefaultMinutes} and ${this.maxDefaultMinutes}.`);
      return;
    }

    const request: CreateActivityTypeRequest | UpdateActivityTypeRequest = {
      name: trimmedName,
      kind: this.kind,
      category: this.category,
      scheduleType: isTimeBlock ? this.scheduleType : 'PRIMARY',
      defaultMinutes: isTimeBlock ? this.defaultMinutes : null,
    };

    const editingId = this.editingId();
    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);

    const operation = editingId
      ? this.activityTypeService.update(editingId, request)
      : this.activityTypeService.create(request);

    operation.subscribe({
      next: () => {
        this.success.set(editingId ? 'Activity type updated.' : 'Activity type created.');
        this.clearForm();
        this.editingId.set(null);
        this.saving.set(false);
        this.silentRefresh();
      },
      error: (err: unknown) => {
        this.error.set(
          describeGenericApiError(
            err,
            editingId ? 'Failed to update activity type' : 'Failed to create activity type'
          )
        );
        this.saving.set(false);
      },
    });
  }

  delete(activityType: ActivityType): void {
    const confirmed = this.confirmDialog.confirm(
      'Delete "' + activityType.name + '"? This removes it from active lists.'
    );
    if (!confirmed) return;

    this.setDeleting(activityType.id, true);
    this.error.set(null);
    this.success.set(null);

    this.activityTypeService.delete(activityType.id).subscribe({
      next: () => {
        if (this.editingId() === activityType.id) {
          this.startCreate();
        }
        this.success.set('Activity type deleted.');
        this.setDeleting(activityType.id, false);
        this.silentRefresh();
      },
      error: (err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to delete activity type'));
        this.setDeleting(activityType.id, false);
      },
    });
  }

  isDeleting(activityTypeId: string): boolean {
    return this.deletingById()[activityTypeId] === true;
  }

  canMoveHabit(index: number, direction: -1 | 1): boolean {
    if (this.reordering() || this.saving()) {
      return false;
    }
    const nextIndex = index + direction;
    return nextIndex >= 0 && nextIndex < this.habitActivityTypes().length;
  }

  moveHabit(index: number, direction: -1 | 1): void {
    if (!this.canMoveHabit(index, direction)) {
      return;
    }
    const reordered = [...this.habitActivityTypes()];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(index + direction, 0, moved);
    const orderedIds = reordered.map((habit) => habit.id);
    const previous = this.activityTypes();
    const nextOrders = new Map(reordered.map((habit, displayOrder) => [habit.id, displayOrder]));
    this.activityTypes.set(previous.map((activityType) => {
      const displayOrder = nextOrders.get(activityType.id);
      return displayOrder === undefined ? activityType : { ...activityType, displayOrder };
    }));
    this.reordering.set(true);
    this.error.set(null);
    this.success.set(null);
    this.activityTypeService.reorder(orderedIds, { kind: 'HABIT_COUNTER' }).subscribe({
      next: (updatedHabits) => {
        const updatedById = new Map(updatedHabits.map((habit) => [habit.id, habit]));
        this.activityTypes.update((current) => current.map((activityType) => updatedById.get(activityType.id) ?? activityType));
        this.reordering.set(false);
      },
      error: (err: unknown) => {
        this.activityTypes.set(previous);
        this.error.set(describeGenericApiError(err, 'Failed to reorder habits'));
        this.reordering.set(false);
      }
    });
  }

  private setDeleting(activityTypeId: string, deleting: boolean): void {
    this.deletingById.update((state) => {
      if (!deleting) {
        const next = { ...state };
        delete next[activityTypeId];
        return next;
      }
      return { ...state, [activityTypeId]: true };
    });
  }

  private clearForm(): void {
    this.name = '';
    this.kind = 'TIME_BLOCK';
    this.category = 'WORK';
    this.scheduleType = this.enabledScheduleTypes()[0] ?? 'PRIMARY';
    this.defaultMinutes = null;
  }

  resolveScheduleTypeLabel(scheduleType: ScheduleType): string {
    const group = this.scheduleGroups().find((entry) => entry.ordinal === scheduleType);
    if (!group) {
      return scheduleType;
    }
    return resolveScheduleGroupLabel(group);
  }

  onKindChange(kind: ActivityTypeKind): void {
    this.kind = kind;
    if (kind !== 'TIME_BLOCK') {
      this.scheduleType = 'PRIMARY';
      this.defaultMinutes = null;
    }
  }

  private loadScheduleGroups(): void {
    this.meService.getMe().subscribe({
      next: (response) => {
        this.scheduleGroups.set(response.settings?.scheduleGroups ?? this.scheduleGroups());
        if (!this.enabledScheduleTypes().includes(this.scheduleType)) {
          this.scheduleType = this.enabledScheduleTypes()[0] ?? 'PRIMARY';
        }
      }
    });
  }

  private silentRefresh(): void {
    this.refreshing.set(true);
    this.activityTypeService.list().subscribe({
      next: (activityTypes) => {
        this.activityTypes.set(activityTypes);
        this.refreshing.set(false);
      },
      error: (err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to load activity types'));
        this.refreshing.set(false);
      },
    });
  }
}
